import { Moment } from 'moment';
import { Ruleset } from '../../../common/enums/Ruleset';
import { APPLICATION_TRACE_TYPES, TraceType } from '../../../common/enums/TraceType';
import { Profile } from '../../../common/models/Profile';
import { CallLimiting } from '../../../common/reports/models/CallLimiting';
import { Coverage } from '../../../common/reports/models/Coverage';
import { Project } from './valueObjects/Project';
import { ReportVersion } from './valueObjects/ReportVersion';
import { Slot } from './valueObjects/Slot';
import { Trace } from './valueObjects/Trace';

export class Report {
  constructor(
    private readonly id: number,
    private readonly name: string,
    private readonly createdAt: Moment,
    private readonly traceType: TraceType | null,
    private readonly trace: Trace | null,
    private readonly profile: Profile | null,
    private readonly project: Project | null,
    private readonly slot: Slot | null,
    private readonly ruleset: Ruleset | null,
    private readonly versions: ReportVersion[]
  ) {}

  getId() {
    return this.id;
  }

  getName() {
    return this.name;
  }

  getCreatedAt() {
    return this.createdAt;
  }

  getTraceType() {
    return this.traceType;
  }

  getTrace() {
    return this.trace;
  }

  getProfile() {
    return this.profile;
  }

  getProject() {
    return this.project;
  }

  getSlot() {
    return this.slot;
  }

  getRuleset() {
    return this.ruleset;
  }

  getVersions() {
    return this.versions;
  }

  canCompare() {
    const traceType = this.getTraceType();
    if (traceType === null) {
      return false;
    }
    return APPLICATION_TRACE_TYPES.includes(traceType);
  }
}

export class ReportPending extends Report {}

export class ReportDone extends Report {
  constructor(
    id: number,
    name: string,
    createdAt: Moment,
    traceType: TraceType | null,
    trace: Trace | null,
    profile: Profile | null,
    project: Project | null,
    slot: Slot | null,
    ruleset: Ruleset | null,
    versions: ReportVersion[],
    private readonly completedAt: Moment,
    private readonly nonsensitiveSalt: string | null,
    private readonly coverage: Coverage | null,
    private readonly callLimiting: CallLimiting | null,
    private readonly issueCount: number,
    private readonly keysCount: number,
    private readonly certificatesCount: number,
    private readonly tlsHandshakesCount: number,
    private readonly sshHandshakesCount: number,
    private readonly ciphersuitesCount: number,
    private readonly activeExceptionsCount: number,
    private readonly operationsCount: number
  ) {
    super(id, name, createdAt, traceType, trace, profile, project, slot, ruleset, versions);
  }

  getCompletedAt() {
    return this.completedAt;
  }

  getNonsensitiveSalt() {
    return this.nonsensitiveSalt;
  }

  getCoverage() {
    return this.coverage;
  }

  getCallLimiting() {
    return this.callLimiting;
  }

  getIssuesCount() {
    return this.issueCount;
  }

  getKeysCount() {
    return this.keysCount;
  }

  getCertificatesCount() {
    return this.certificatesCount;
  }

  getTlsHandshakesCount() {
    return this.tlsHandshakesCount;
  }

  getSshHandshakesCount() {
    return this.sshHandshakesCount;
  }

  getCiphersuitesCount() {
    return this.ciphersuitesCount;
  }

  getActiveExceptionsCount() {
    return this.activeExceptionsCount;
  }

  getOperationsCount() {
    return this.operationsCount;
  }
}

export class ReportFailed extends Report {
  constructor(
    id: number,
    name: string,
    createdAt: Moment,
    traceType: TraceType | null,
    trace: Trace | null,
    profile: Profile | null,
    project: Project | null,
    slot: Slot | null,
    ruleset: Ruleset | null,
    versions: ReportVersion[],
    private readonly reason: string
  ) {
    super(id, name, createdAt, traceType, trace, profile, project, slot, ruleset, versions);
  }

  getReason() {
    return this.reason;
  }
}
